import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FolderOpen,
  MoreVertical,
  Loader2,
  AlertTriangle,
  User,
  LogOut,
  Settings,
  ChevronDown
} from 'lucide-react';
import { supabase } from './supabaseClient'; 
import { Button } from './components/Button';
import { Card } from './components/Card';

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchProjects(session.user.id);
      } else {
        navigate('/auth');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
      } else {
        navigate('/auth');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const fetchProjects = async (userId) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch by created_at initially
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Client-side sort based on JSON 'last_edited' or 'lastActive'
      const sortedData = (data || []).sort((a, b) => {
        const timeA = new Date(a.info?.last_edited || a.info?.lastActive || a.created_at).getTime();
        const timeB = new Date(b.info?.last_edited || b.info?.lastActive || b.created_at).getTime();
        return timeB - timeA; // Descending order
      });

      setProjects(sortedData);
    } catch (err) {
      console.error("Supabase Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getUserInitials = () => {
    if (!user) return <User size={18} />;
    const name = user.user_metadata?.full_name || user.email;
    return name ? name[0].toUpperCase() : <User size={18} />;
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <img
                src="/logo512.png"
                alt="Hollowpad"
                className="w-10 h-10 rounded-lg object-contain bg-slate-900 border border-slate-800 shadow-lg shadow-blue-900/20"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <div className="w-10 h-10 bg-blue-600 rounded-lg hidden items-center justify-center shadow-lg shadow-blue-900/50">
              <span className="font-bold text-white">B</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Hollowpad Studio</h1>
          </div>

          <div className="flex items-center gap-4 w-full md:w-auto">
            <button
              onClick={() => navigate('/console/new')}
              className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-lg font-bold shadow-lg shadow-blue-900/30 transition-all flex items-center justify-center gap-2"
            >
              <Plus size={20} /> New Project
            </button>

            <div className="h-8 w-px bg-slate-800 hidden md:block" />

            {/* Account Dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="flex items-center gap-2 p-1 rounded-full bg-slate-900 border border-slate-800 hover:border-slate-700 transition-all shadow-lg hover:shadow-blue-900/10"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-sm font-bold shadow-inner">
                  {getUserInitials()}
                </div>
                <ChevronDown size={14} className={`text-slate-500 mr-2 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {isMenuOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl z-50 py-2 animate-in fade-in zoom-in duration-150 origin-top-right backdrop-blur-xl">
                  <div className="px-4 py-3 border-b border-slate-800 mb-2">
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Signed in as</p>
                    <p className="text-sm font-bold truncate text-white mt-0.5">{user?.email}</p>
                  </div>

                  <button
                    onClick={() => { navigate('/settings'); setIsMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                  >
                    <Settings size={16} className="text-slate-500" /> Account Settings
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-400 hover:bg-red-950/20 hover:text-red-300 transition-colors"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 text-red-200 p-4 rounded-xl mb-8 flex items-center gap-3">
            <AlertTriangle size={24} />
            <div>
              <p className="font-bold">Database Error</p>
              <p className="text-sm font-mono">{error}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {loading ? (
             <div className="text-slate-500 col-span-full flex flex-col items-center py-20 gap-4">
               <Loader2 className="animate-spin text-blue-500" size={32} />
               <p>Loading Projects...</p>
             </div>
          ) : projects.length === 0 ? (
             <div className="text-slate-500 col-span-full text-center py-20 border-2 border-dashed border-slate-800 rounded-xl">
               <p className="text-lg mb-2">No projects found.</p>
               <p className="text-sm text-slate-600">Create one to get started.</p>
             </div>
          ) : (
             projects.map((project) => (
                <LiveProjectCard key={project.id} project={project} />
             ))
          )}
        </div>
      </div>
    </div>
  );
}

// --- UPDATED LIVE PROJECT CARD ---
function LiveProjectCard({ project }) {
    const navigate = useNavigate();

    // Helper to extract the best available timestamp from the JSON blob
    const getBestTimestamp = (dataObj) => {
        // Priority: info.last_edited (ISO) > info.lastActive (Timestamp) > created_at
        return dataObj.info?.last_edited || dataObj.info?.lastActive || dataObj.created_at;
    };

    const [liveData, setLiveData] = useState({
        status: project.info?.status || 'idle',
        lastUpdated: getBestTimestamp(project), 
        thumbnail: project.thumbnail || null,
        title: project.info?.title || "Untitled Project"
    });

    useEffect(() => {
        let isMounted = true;

        const checkUpdates = async () => {
            try {
                // Fetch info column explicitly
                const { data, error } = await supabase
                    .from('projects')
                    .select('info, thumbnail, created_at')
                    .eq('id', project.id)
                    .single();

                if (error) throw error;

                if (isMounted && data) {
                    setLiveData({
                        status: data.info?.status || 'idle',
                        lastUpdated: getBestTimestamp(data),
                        thumbnail: data.thumbnail,
                        title: data.info?.title || "Untitled Project"
                    });
                }
            } catch (err) {
                console.warn(`Polling error for project ${project.id}:`, err);
            }
        };

        // Poll every 2 seconds for live status updates
        const intervalId = setInterval(checkUpdates, 2000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [project.id]);

    const handleOpenProject = () => {
        navigate(`/console/project/${project.id}`);
    };

    const handleMenuClick = (e) => {
        e.stopPropagation();
    };

    const dbStatus = (liveData.status || '').toLowerCase();
    const isWorking = (dbStatus !== 'idle' && dbStatus !== 'complete' && dbStatus !== 'error' && dbStatus !== '');

    const getTimeAgo = (timestamp) => {
        if (!timestamp) return 'Unknown';

        const date = new Date(timestamp);
        const now = new Date();
        const seconds = Math.floor((now - date) / 1000);

        if (seconds < 60) return 'Just now';
        
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    return (
        <Card
            onClick={handleOpenProject}
            className={`
                bg-slate-900 border transition-all cursor-pointer group relative overflow-hidden shadow-sm hover:shadow-blue-900/10 flex flex-col justify-between h-full
                ${isWorking ? 'border-amber-500/30' : 'border-slate-800 hover:border-blue-500/50 hover:bg-slate-900/80'}
            `}
        >
            <div className="absolute top-0 right-0 p-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={handleMenuClick} className="text-slate-400 hover:text-white bg-slate-950/50 rounded p-1 backdrop-blur-sm">
                    <MoreVertical size={20} />
                </button>
            </div>

            <div className="flex items-start gap-4 mb-4">
                <div className={`
                    rounded-xl transition-colors shadow-inner overflow-hidden flex items-center justify-center shrink-0
                    ${isWorking
                        ? 'bg-amber-500/10 text-amber-500 p-3 w-14 h-14'
                        : liveData.thumbnail
                            ? 'w-14 h-14 bg-slate-950 border border-slate-700'
                            : 'bg-slate-800 text-blue-400 group-hover:text-blue-300 p-3 w-14 h-14'
                    }
                `}>
                    {isWorking ? (
                        <Loader2 size={24} className="animate-spin" />
                    ) : liveData.thumbnail ? (
                        <img src={liveData.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                    ) : (
                        <FolderOpen size={24} />
                    )}
                </div>

                <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold group-hover:text-blue-400 transition-colors truncate">
                        {liveData.title}
                    </h3>
                    <p className="text-slate-500 text-xs mt-1 flex items-center gap-1">
                        Last edited {getTimeAgo(liveData.lastUpdated)}
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-800">
                {isWorking ? (
                    <span className="text-xs font-bold px-2 py-1 rounded border bg-amber-500/10 text-amber-500 border-amber-500/20 flex items-center gap-2 transition-all">
                        <Loader2 size={12} className="animate-spin" />
                        {dbStatus === 'idle' ? 'Working...' : liveData.status}
                    </span>
                ) : (
                    <span className="text-xs font-mono px-2 py-1 rounded border bg-slate-800/50 text-slate-500 border-slate-700 flex items-center gap-2 transition-all">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                        Idle
                    </span>
                )}

                <Button variant="ghost" className="text-xs h-8 hover:bg-slate-800 pointer-events-none">
                    Open Studio
                </Button>
            </div>
        </Card>
    );
}
